import type { FC } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type Hls from "hls.js"
import classNames from "classnames"
import { useIsVisible } from "~/hooks/useIsVisible"
import { MuteButton } from "~/components/design-system/atoms/MuteButton"

declare module "react" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface VideoHTMLAttributes<T> {
    fetchpriority?: "high" | "low" | "auto"
  }
}

const VideoPlayer: FC<{
  src: string
  poster?: string
  controls?: boolean
  minimalControls?: boolean
  showMuteButton?: boolean
  loop?: boolean
  autoPlay?: boolean
  muted?: boolean
  playButton?: boolean
  playButtonColor?: string
  height?: number
  width?: number
  accessibilityLabel?: string
  fetchPriority?: "high" | "low" | "auto"
  active?: boolean
}> = ({
  src,
  poster,
  controls,
  minimalControls,
  showMuteButton,
  loop,
  autoPlay,
  muted = true,
  playButton,
  playButtonColor,
  height,
  width,
  accessibilityLabel,
  fetchPriority,
  active = true
}) => {
  const { isVisible, targetRef } = useIsVisible(
    {
      root: null,
      rootMargin: "200px",
      threshold: 0.1
    },
    false
  )
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)

  const startVideoOnIntersect = useCallback(async () => {
    if (!videoRef.current) return
    try {
      if (autoPlay) {
        await videoRef.current.play()
      }
    } catch (e) {
      // do nothing
    }
  }, [autoPlay])

  const stopVideo = useCallback(() => {
    if (!videoRef.current) return
    try {
      videoRef.current.pause()
    } catch (e) {
      // do nothing
    }
  }, [])

  const [play, setPlay] = useState(false)

  useEffect(() => {
    if (isVisible && active) {
      startVideoOnIntersect()
    } else {
      stopVideo()
    }
  }, [isVisible, active, startVideoOnIntersect, stopVideo])

  useEffect(() => {
    let isMounted = true

    if (!videoRef.current) {
      return
    }

    if (
      videoRef.current.canPlayType("application/vnd.apple.mpegurl") ===
      "probably"
    ) {
      videoRef.current.src = src
    } else {
      // Lazy load hls.js only when needed
      import("hls.js")
        .then(({ default: Hls }) => {
          if (!isMounted || !videoRef.current) {
            return
          }
          if (Hls.isSupported()) {
            // Clean up previous hls instance if it exists
            hlsRef.current?.destroy()
            const hls = new Hls({
              lowLatencyMode: true,
              enableWorker: true,
              maxLoadingDelay: 4,
              minAutoBitrate: 0,
              ...(fetchPriority
                ? {
                    fetchSetup: (context, initParams) => {
                      initParams.priority = fetchPriority
                      return new Request(context.url, initParams)
                    }
                  }
                : {})
            })
            hlsRef.current = hls
            hls.loadSource(src)
            hls.attachMedia(videoRef.current)
          } else {
            console.error("This is a legacy browser that doesn't support MSE")
          }
        })
        .catch(error => {
          console.error("Failed to load hls.js:", error)
        })
    }

    return () => {
      isMounted = false
      hlsRef.current?.destroy()
      hlsRef.current = null
    }
  }, [src, fetchPriority])

  const autoPlayOptions: React.VideoHTMLAttributes<HTMLVideoElement> = useMemo(
    () =>
      autoPlay
        ? {
            autoPlay: true,
            playsInline: true,
            muted: true
          }
        : {},
    [autoPlay]
  )

  useEffect(() => {
    if (playButton) {
      if (play) {
        videoRef.current?.play()
      } else {
        videoRef.current?.pause()
      }
    }
  }, [playButton, play])

  const handleClick = () => {
    if (playButton) {
      setPlay(!play)
    }
  }

  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(muted)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [controlsVisible, setControlsVisible] = useState(true)
  // Whether the video has loaded its first frame. Until then the poster/video
  // has not painted and only the gallery placeholder is visible, so the minimal
  // controls must stay hidden (otherwise they float over the empty placeholder).
  const [isReady, setIsReady] = useState(false)
  const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!minimalControls || !video) return

    let frame = 0

    const tick = () => {
      setCurrentTime(video.currentTime)
      frame = requestAnimationFrame(tick)
    }
    const startTicking = () => {
      if (!frame) frame = requestAnimationFrame(tick)
    }
    const stopTicking = () => {
      if (frame) {
        cancelAnimationFrame(frame)
        frame = 0
      }
    }

    const handlePlay = () => {
      setIsPlaying(true)
      startTicking()
    }
    const handlePause = () => {
      setIsPlaying(false)
      stopTicking()
      setCurrentTime(video.currentTime)
    }
    const handleTimeUpdate = () => setCurrentTime(video.currentTime)
    const handleDuration = () =>
      setDuration(Number.isFinite(video.duration) ? video.duration : 0)
    const handleReady = () => setIsReady(true)

    // HAVE_CURRENT_DATA or greater: the first frame is already available.
    if (video.readyState >= 2) setIsReady(true)

    video.addEventListener("play", handlePlay)
    video.addEventListener("pause", handlePause)
    video.addEventListener("timeupdate", handleTimeUpdate)
    video.addEventListener("loadedmetadata", handleDuration)
    video.addEventListener("durationchange", handleDuration)
    video.addEventListener("loadeddata", handleReady)

    if (!video.paused) startTicking()

    return () => {
      stopTicking()
      video.removeEventListener("play", handlePlay)
      video.removeEventListener("pause", handlePause)
      video.removeEventListener("timeupdate", handleTimeUpdate)
      video.removeEventListener("loadedmetadata", handleDuration)
      video.removeEventListener("durationchange", handleDuration)
      video.removeEventListener("loadeddata", handleReady)
    }
  }, [minimalControls])

  const togglePlay = () => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) {
      video.play()
    } else {
      video.pause()
    }
  }

  const toggleMute = () => {
    const video = videoRef.current
    if (!video) return
    const nextMuted = !video.muted
    video.muted = nextMuted
    setIsMuted(nextMuted)
  }

  const handleSeek = (event: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current
    if (!video) return
    const time = Number(event.target.value)
    video.currentTime = time
    setCurrentTime(time)
  }

  // Auto-hide the minimal controls after a short idle period while playing;
  // any pointer/touch activity reveals them again and restarts the timer.
  const revealControls = useCallback(() => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current)
    setControlsVisible(true)
    const video = videoRef.current
    if (video && !video.paused) {
      hideTimeout.current = setTimeout(() => setControlsVisible(false), 2500)
    }
  }, [])

  useEffect(() => {
    if (!minimalControls) return
    revealControls()
    return () => {
      if (hideTimeout.current) clearTimeout(hideTimeout.current)
    }
  }, [minimalControls, isPlaying, revealControls])

  return (
    <div
      className="relative"
      {...(playButton
        ? {
            onKeyDown: handleClick,
            onClick: handleClick,
            role: "button",
            tabIndex: 0,
            "aria-label": play ? "pause video" : "play video"
          }
        : {})}
      {...(minimalControls
        ? {
            onMouseEnter: revealControls,
            onMouseMove: revealControls,
            onTouchStart: revealControls
          }
        : {})}
    >
      {playButton && (
        <div
          className={classNames(
            "ease duration-400 absolute bottom-0 left-0 right-0 top-0 m-auto flex h-20 w-20 items-center justify-center rounded-full transition-all",
            play ? "opacity-0" : "opacity-90"
          )}
          style={{
            backgroundColor: playButtonColor ? playButtonColor : "#000"
          }}
        >
          <img
            loading="lazy"
            className="ml-1"
            src="/images/play.svg"
            alt="play icon"
          />
        </div>
      )}
      <span ref={targetRef as any}>
        <video
          className="w-full h-full object-cover"
          {...autoPlayOptions}
          controls={minimalControls ? false : controls}
          loop={loop}
          preload="auto"
          poster={poster}
          fetchpriority={fetchPriority}
          ref={videoRef}
          muted={isMuted}
          height={height}
          width={width}
          aria-label={accessibilityLabel}
        />
      </span>
      {minimalControls && isReady && (
        <div
          className={classNames(
            "absolute bottom-0 left-0 right-0 flex items-center gap-3 bg-gradient-to-t from-black/60 to-transparent px-4 py-3 transition-opacity duration-300",
            controlsVisible ? "opacity-100" : "pointer-events-none opacity-0"
          )}
        >
          <button
            type="button"
            onClick={togglePlay}
            aria-label={isPlaying ? "pause video" : "play video"}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center text-white"
          >
            {isPlaying ? (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <rect x="6" y="5" width="4" height="14" rx="1" />
                <rect x="14" y="5" width="4" height="14" rx="1" />
              </svg>
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M8 5.14v13.72a1 1 0 0 0 1.54.84l10.29-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14Z" />
              </svg>
            )}
          </button>
          <div className="relative flex h-3 w-full items-center">
            <div className="absolute inset-x-0 h-1 rounded-full bg-white/30" />
            <div
              className="absolute left-0 h-1 rounded-full bg-white"
              style={{
                width: duration ? `${(currentTime / duration) * 100}%` : "0%"
              }}
            />
            <input
              type="range"
              min={0}
              max={duration || 0}
              step="any"
              value={currentTime}
              onChange={handleSeek}
              aria-label="video progress"
              className="relative h-1 w-full cursor-pointer appearance-none rounded-full border-0 bg-transparent outline-none [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-white [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-white"
            />
          </div>
          {showMuteButton && (
            <MuteButton
              isMuted={isMuted}
              onClick={toggleMute}
              className="h-8 w-8"
              iconClassName="h-5 w-5"
            />
          )}
        </div>
      )}
    </div>
  )
}

export default VideoPlayer
